import { ApplicationNoteDto, CreateApplicationNoteDto } from '@inosio/payments-sdk';
import { Typography, IconButton, Grid, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { format } from 'date-fns';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectApplicationsState,
  getAllApplicationNotes,
  clearApplicationNotesStates,
  createApplicationNote,
  deleteApplicationNote,
} from '../../../../../resui/applications/sagas/pm/applicationsSlice';
import CreateDeleteNoteModal, { NoteModalActionTypeEnum } from '../../../../../shared/Modal/CreateDeleteNoteModal';
import SpinnerWithNoConfirmation from '../../../../../shared/Modal/spinnerWithNoConfirmation';
import AddNoteIcon from '../../../../../icons/AddNoteIcon.svg';
import HelpIcon from '@mui/icons-material/Help';

export interface ApplicationDetailsNotesProps {
  notes: ApplicationNoteDto[];
  appUuid: string;
}

export default function ApplicationDetailsNotes(props: ApplicationDetailsNotesProps) {
  const dispatch = useDispatch();
  const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
  const [noteModalActionType, setNoteModalActionType] = useState<NoteModalActionTypeEnum>(NoteModalActionTypeEnum.Create);
  const [noteDescription, setNoteDescription] = useState('');
  const [noteAuthorName, setNoteAuthorName] = useState('');
  const [noteDescriptionValidationFailed, setNoteDescriptionValidationFailed] = useState(false);
  const [noteIdToBeDeleted, setNoteIdToBeDeleted] = useState('');
  const [localError, setLocalError] = useState('');

  const {
    isCreatingApplicationNote,
    isCreatingApplicationNoteFailed,
    isCreatingApplicationNoteSuccess,
    isDeletingApplicationNote,
    isDeletingApplicationNoteSuccess,
    isFetchingAllApplicationNotesSuccess,
    isFetchingAllApplicationNotesFailed,
    isDeletingApplicationNoteFailed,
    isFetchingAllApplicationNotes,
  } = useSelector(selectApplicationsState);

  //if creating a note is success, refresh all local states and refetch all the notes
  useEffect(() => {
    if (isCreatingApplicationNoteSuccess || isDeletingApplicationNoteSuccess) {
      setOpenCreateNoteModal(false);
      setNoteDescription('');
      setNoteAuthorName('');
      setNoteIdToBeDeleted('');
      dispatch({
        type: getAllApplicationNotes.type,
        payload: {
          applicationUuid: props.appUuid,
        },
      });
    }
  }, [isCreatingApplicationNoteSuccess, isDeletingApplicationNoteSuccess]);

  useEffect(() => {
    if (isFetchingAllApplicationNotesSuccess) {
      setLocalError('');
    } else if (isFetchingAllApplicationNotesFailed) {
      setLocalError('Something went wrong while fetching notes. Please try again later or contact support.');
    }
  }, [isFetchingAllApplicationNotesFailed, isFetchingAllApplicationNotesSuccess]);

  return (
    <Box sx={{ width: '25%', overflow: 'auto', height: 'calc( 100vh - 294px )', borderLeft: '1px solid lightgrey' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 50, width: '100%' }}>
        <Typography
          sx={{
            fontFamily: 'Work Sans',
            fontStyle: 'normal',
            fontWeight: 600,
            fontSize: '18px',
            color: '#14142B',
            marginRight: '10px',
            ml: 2,
          }}
        >
          Note Pad
          <Tooltip title="These notes are to help you and your staff stay updated on this application. They will not be shared with the applicants." placement="right">
            <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
          </Tooltip>
        </Typography>
        <IconButton
          onClick={() => {
            setNoteModalActionType(NoteModalActionTypeEnum.Create);
            setOpenCreateNoteModal(true);
          }}
        >
          <img src={AddNoteIcon} style={{ height: '26px', width: '26px' }} alt={'AddNoteIcon'} />
        </IconButton>
      </Box>
      {isFetchingAllApplicationNotes ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box
          sx={{
            height: 'calc( 100% - 50px )',
            pl: 2,
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: 7,
            },
            '&::-webkit-scrollbar-track': {
              boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
              borderRadius: '24px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'darkgrey',
              borderRadius: '24px',
            },
          }}
        >
          <Grid container display="flex" justifyContent="center">
            {localError && (
              <Grid item xs={12} display="flex" justifyContent="center">
                <Typography
                  sx={{
                    fontFamily: 'Work Sans !important',
                    fontStyle: 'normal !important',
                    fontWeight: '600 !important',
                    fontSize: '16px !important',
                    lineHeight: '22px !important',
                    color: '#DB3939 !important',
                    margin: '20px 20px 20px !important',
                  }}
                >
                  {localError}
                </Typography>
              </Grid>
            )}
            {props.notes?.map((note) => {
              return (
                <Grid item xs={12} key={note.Id}>
                  <Grid
                    onClick={() => {
                      setNoteModalActionType(NoteModalActionTypeEnum.Delete);
                      setOpenCreateNoteModal(true);
                      setNoteAuthorName(note.AuthorName);
                      setNoteDescription(note.Note);
                      setNoteIdToBeDeleted(note.Id);
                    }}
                    item
                    p={1}
                    sx={{ backgroundColor: '#F1F0F5', borderRadius: '10px', cursor: 'pointer' }}
                  >
                    <Typography
                      sx={{
                        width: '100%',
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 400,
                        fontSize: '14px',
                        color: '#14142B',
                        marginRight: '10px',
                        wordWrap: 'break-word',
                      }}
                    >
                      {note.Note}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} display="Flex" justifyContent="flex-end" mx={1} mb={1}>
                    <Typography
                      sx={{
                        fontFamily: 'Work Sans',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        fontSize: '10px',
                        color: '#14142B',
                      }}
                    >
                      {`${note.AuthorName} ${format(new Date(note.CreatedDate), "MM/dd/yyyy 'at' h:mm a")}`}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      <CreateDeleteNoteModal
        open={openCreateNoteModal}
        onClose={() => {
          setOpenCreateNoteModal(false);
          setNoteDescription('');
          setNoteDescriptionValidationFailed(false);
          dispatch(clearApplicationNotesStates());
        }}
        actionType={noteModalActionType}
        handleCreateNote={() => {
          dispatch({
            type: createApplicationNote.type,
            payload: {
              applicationUuid: props.appUuid,
              createApplicationNoteDto: {
                Note: noteDescription,
              } as CreateApplicationNoteDto,
            },
          });
        }}
        handleDeleteNote={() => {
          dispatch({
            type: deleteApplicationNote.type,
            payload: {
              applicationUuid: props.appUuid,
              noteId: noteIdToBeDeleted,
            },
          });
        }}
        isDeletingNote={isDeletingApplicationNote}
        isCreatingNote={isCreatingApplicationNote}
        noteAuthorName={noteAuthorName}
        isCreatingNoteFailed={isCreatingApplicationNoteFailed}
        isDeletingNoteFailed={isDeletingApplicationNoteFailed}
        noteDescription={noteDescription}
        handleNoteChange={(note) => {
          if (!note) setNoteDescriptionValidationFailed(true);
          else setNoteDescriptionValidationFailed(false);
          setNoteDescription(note);
        }}
        noteDescriptionValidationFailed={noteDescriptionValidationFailed}
      />
    </Box>
  );
}
